import type { Finanziamento, ProfiloFinanziario } from '../types';
import { parseDecimal } from '../utils/pricing';

/**
 * Calculate monthly payment using French amortization (ammortamento francese)
 * Formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
 * Where:
 *   M = monthly payment
 *   P = principal loan amount
 *   r = monthly interest rate (annual rate / 12)
 *   n = total number of payments (years * 12)
 */
export function calculateMortgagePayment(
  principal: number,
  annualRate: number,
  years: number
): number {
  if (principal <= 0 || years <= 0) return 0;
  if (annualRate === 0) return principal / (years * 12);

  const monthlyRate = annualRate / 12;
  const totalPayments = years * 12;

  const payment =
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments))) /
    (Math.pow(1 + monthlyRate, totalPayments) - 1);

  return payment;
}

/**
 * Calculate loan payment (prestito personale)
 * Same formula as mortgage but typically shorter duration
 */
export function calculateLoanPayment(
  principal: number,
  annualRate: number,
  years: number
): number {
  return calculateMortgagePayment(principal, annualRate, years);
}

/**
 * Calculate total interest paid over the life of the loan
 */
export function calculateTotalInterest(
  principal: number,
  monthlyPayment: number,
  years: number
): number {
  const totalPaid = monthlyPayment * years * 12;
  return totalPaid - principal;
}

/**
 * Calculate total amount paid (principal + interest)
 */
export function calculateTotalPaid(monthlyPayment: number, years: number): number {
  return monthlyPayment * years * 12;
}

/**
 * Generate amortization schedule
 */
export interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}

export function generateAmortizationSchedule(
  principal: number,
  annualRate: number,
  years: number
): AmortizationRow[] {
  const schedule: AmortizationRow[] = [];
  const monthlyRate = annualRate / 12;
  const totalPayments = years * 12;
  const monthlyPayment = calculateMortgagePayment(principal, annualRate, years);

  let balance = principal;

  for (let month = 1; month <= totalPayments; month++) {
    const interest = balance * monthlyRate;
    const principalPaid = monthlyPayment - interest;
    balance = Math.max(0, balance - principalPaid);

    schedule.push({
      month,
      payment: monthlyPayment,
      principal: principalPaid,
      interest,
      balance,
    });
  }

  return schedule;
}

/**
 * Calculate liquidity (saldo liquidità)
 * Cash available = total financing - total costs
 */
export function calculateLiquidity(
  totalCosts: number,
  totalFinancing: number,
  initialCash: number = 0
): number {
  return initialCash + totalFinancing - totalCosts;
}

/**
 * Calculate rata from Finanziamento object
 */
export function calculateRataFromFinanziamento(f: Finanziamento): number {
  const importo = parseDecimal(f.importo);
  const tasso = parseDecimal(f.tassoInteresse);
  const durata = f.durataAnni;

  if (f.rataMensile) {
    return parseDecimal(f.rataMensile);
  }

  return calculateMortgagePayment(importo, tasso, durata);
}

/**
 * Rata/scadenza generata dal piano di ammortamento di un finanziamento.
 */
export interface RataScadenza {
  finanziamentoId: string;
  finanziamentoNome: string;
  finanziamentoTipo: Finanziamento['tipo'];
  numero: number; // 1-based
  totaleRate: number;
  date: string; // ISO yyyy-mm-dd (giorno = quello di dataPrimaRata)
  importo: number;
}

/**
 * Genera l'elenco completo delle rate (scadenze) di un finanziamento, a partire
 * da `dataPrimaRata` per `durataAnni * 12` mensilità. Il giorno del mese è quello
 * della prima rata (es. il 19). Fonte di verità unica per Flussi, Scadenze e Dashboard.
 * Ritorna [] se manca la data prima rata o la rata è nulla.
 */
export function generateRateScadenze(f: Finanziamento): RataScadenza[] {
  if (!f.dataPrimaRata) return [];

  const rata = f.rataMensile ? parseDecimal(f.rataMensile) : calculateRataFromFinanziamento(f);
  if (rata <= 0) return [];

  const totaleRate = Math.max(1, f.durataAnni * 12);
  const start = new Date(f.dataPrimaRata);
  const year = start.getUTCFullYear();
  const month = start.getUTCMonth();
  const day = start.getUTCDate();

  const rate: RataScadenza[] = [];
  for (let i = 0; i < totaleRate; i++) {
    const d = new Date(Date.UTC(year, month + i, day));
    rate.push({
      finanziamentoId: f.id,
      finanziamentoNome: f.nome,
      finanziamentoTipo: f.tipo,
      numero: i + 1,
      totaleRate,
      date: d.toISOString().split('T')[0],
      importo: rata,
    });
  }
  return rate;
}

/**
 * Aggrega e ordina per data le rate di più finanziamenti (solo mutui/prestiti con
 * dataPrimaRata valorizzata). Opzionalmente filtra le sole scadenze da `from` in poi.
 */
export function generateAllRateScadenze(
  finanziamenti: Finanziamento[],
  from?: Date
): RataScadenza[] {
  const all = finanziamenti.flatMap((f) => generateRateScadenze(f));
  const fromTs = from ? new Date(from).setHours(0, 0, 0, 0) : null;
  const filtered = fromTs !== null
    ? all.filter((r) => new Date(r.date).getTime() >= fromTs)
    : all;
  return filtered.sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Calculate summary for all financing
 */
export interface FinancingSummary {
  totalImporto: number;
  totalRataMensile: number;
  totalInterest: number;
  totalPaid: number;
  averageRate: number;
  mutui: {
    count: number;
    importo: number;
    rata: number;
  };
  prestiti: {
    count: number;
    importo: number;
    rata: number;
  };
  fondiPropri: {
    count: number;
    importo: number;
  };
}

export function calculateFinancingSummary(
  finanziamenti: Finanziamento[]
): FinancingSummary {
  const summary: FinancingSummary = {
    totalImporto: 0,
    totalRataMensile: 0,
    totalInterest: 0,
    totalPaid: 0,
    averageRate: 0,
    mutui: { count: 0, importo: 0, rata: 0 },
    prestiti: { count: 0, importo: 0, rata: 0 },
    fondiPropri: { count: 0, importo: 0 },
  };

  if (finanziamenti.length === 0) return summary;

  let totalWeightedRate = 0;
  let totalImportoWithRate = 0;

  finanziamenti.forEach((f) => {
    const importo = parseDecimal(f.importo);

    summary.totalImporto += importo;

    if (f.tipo === 'fondi_propri') {
      summary.fondiPropri.count++;
      summary.fondiPropri.importo += importo;
      return;
    }

    const tasso = parseDecimal(f.tassoInteresse);
    const rata = calculateRataFromFinanziamento(f);
    const totalPaid = calculateTotalPaid(rata, f.durataAnni);
    const interest = calculateTotalInterest(importo, rata, f.durataAnni);

    summary.totalRataMensile += rata;
    summary.totalInterest += interest;
    summary.totalPaid += totalPaid;

    totalWeightedRate += tasso * importo;
    totalImportoWithRate += importo;

    if (f.tipo === 'mutuo') {
      summary.mutui.count++;
      summary.mutui.importo += importo;
      summary.mutui.rata += rata;
    } else {
      summary.prestiti.count++;
      summary.prestiti.importo += importo;
      summary.prestiti.rata += rata;
    }
  });

  summary.averageRate =
    totalImportoWithRate > 0 ? totalWeightedRate / totalImportoWithRate : 0;

  return summary;
}

/**
 * Financeability assessment (Valuta Finanziabilità)
 */
export type EsitoSemaforo = 'verde' | 'giallo' | 'rosso';

export interface FattoreFinanziabilita {
  label: string;
  status: EsitoSemaforo;
}

export interface RisultatoFinanziabilita {
  esito: EsitoSemaforo;
  rapporto: number;
  fattori: FattoreFinanziabilita[];
}

export function calcolaFinanziabilita(
  profilo: ProfiloFinanziario,
  finanziamenti: Finanziamento[]
): RisultatoFinanziabilita {
  const fattori: FattoreFinanziabilita[] = [];

  // Calculate total income
  const redditoNetto = parseDecimal(profilo.redditoNettoMensile);
  const coRichiedente = parseDecimal(profilo.redditoCoRichiedente);
  const altreEntrate = parseDecimal(profilo.altreEntrate);
  const redditoTotale = redditoNetto + coRichiedente + altreEntrate;

  // Calculate project installments (only mutuo/prestito)
  const rataProgetto = finanziamenti
    .filter((f) => f.tipo !== 'fondi_propri')
    .reduce((sum, f) => sum + calculateRataFromFinanziamento(f), 0);

  const rateMensiliInCorso = parseDecimal(profilo.rateMensiliInCorso);
  const rataComplessiva = rataProgetto + rateMensiliInCorso;

  // Ratio
  const rapporto = redditoTotale > 0 ? (rataComplessiva / redditoTotale) * 100 : 100;

  // --- Evaluate factors ---

  // Ratio check
  if (rapporto > 40) {
    fattori.push({ label: `Rapporto rata/reddito ${rapporto.toFixed(1)}% (> 40%)`, status: 'rosso' });
  } else if (rapporto > 30) {
    fattori.push({ label: `Rapporto rata/reddito ${rapporto.toFixed(1)}% (30-40%)`, status: 'giallo' });
  } else {
    fattori.push({ label: `Rapporto rata/reddito ${rapporto.toFixed(1)}% (< 30%)`, status: 'verde' });
  }

  // CRIF check
  if (profilo.segnalazioneCrif) {
    fattori.push({ label: 'Segnalazione CRIF presente', status: 'rosso' });
  } else {
    fattori.push({ label: 'Nessuna segnalazione CRIF', status: 'verde' });
  }

  // Contract type + seniority
  const anzianita = profilo.anzianitaMesi ?? 0;

  if (profilo.tipoContratto === 'determinato' && anzianita < 6) {
    fattori.push({ label: `Contratto determinato con anzianità < 6 mesi`, status: 'rosso' });
  } else if (profilo.tipoContratto === 'determinato' || profilo.tipoContratto === 'autonomo') {
    fattori.push({ label: `Contratto ${profilo.tipoContratto}`, status: 'giallo' });
  } else {
    fattori.push({ label: `Contratto ${profilo.tipoContratto}`, status: 'verde' });
  }

  if (anzianita < 12 && anzianita >= 6) {
    fattori.push({ label: `Anzianità lavorativa < 12 mesi`, status: 'giallo' });
  } else if (anzianita >= 12) {
    fattori.push({ label: `Anzianità lavorativa ${anzianita} mesi`, status: 'verde' });
  }

  // Age check
  if (profilo.eta > 65) {
    fattori.push({ label: `Età ${profilo.eta} anni (> 65)`, status: 'giallo' });
  } else {
    fattori.push({ label: `Età ${profilo.eta} anni`, status: 'verde' });
  }

  // Determine overall result
  const hasRosso = fattori.some((f) => f.status === 'rosso');
  const hasGiallo = fattori.some((f) => f.status === 'giallo');

  let esito: EsitoSemaforo = 'verde';
  if (hasRosso) esito = 'rosso';
  else if (hasGiallo) esito = 'giallo';

  return { esito, rapporto, fattori };
}

/**
 * Format rate as percentage string
 */
export function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}
